<script setup>
import { computed } from 'vue'
import {IconSquareArrowRight} from '@tabler/icons-vue'
import {useRouter} from "vue-router";
import { Card, DetailField } from "@/components/atomic";

const router = useRouter()

const props = defineProps({
  passenger: {
    type: Object,
    required: true
  }
})

const formatDateTime = (dateTime) => {
  if (!dateTime) return __DICT__.notAvailable || 'Not available'
  const date = new Date(dateTime)
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'GMT',
    timeZoneName: 'short'
  }).format(date)
}

const linkToTravel = async (coach) => {
  if(coach[0] === 'C' && /\d/.test(coach[1])) {
    router.push(`/coach/${coach}`)
  } else {
    router.push(`/transfer/${coach}`)
  }
}

const accommodationDetails = computed(() => {
  if (!props.passenger?.allocPlaces?.length) return null
  return props.passenger.allocPlaces.find(place => place.accommodation)?.accommodation
})

const outboundTravel = computed(() => {
  if (!props.passenger?.allocPlaces?.length) return null
  return props.passenger.allocPlaces.find(place => place.transport?.type === 'transport-outbound')?.transport
})

const inboundTravel = computed(() => {
  if (!props.passenger?.allocPlaces?.length) return null
  return props.passenger.allocPlaces.find(place => place.transport?.type === 'transport-inbound')?.transport
})
</script>

<template>
  <Card v-if="passenger" :title="__DICT__.travel?.travelDetails || 'Travel Details'" show-status-bar margin="mb-2">
      <div v-if="outboundTravel">
        <h5>{{ __DICT__.travel?.outboundJourney || 'Outbound Journey' }}</h5>
        <template v-if="outboundTravel.transportType">
          <DetailField :label="__DICT__.travel?.type || 'Type'"
                       :value="outboundTravel.transportType || (__DICT__.notAvailable || 'Not available')" />
          <DetailField v-if="outboundTravel.equipmentCarriage"
                       :label="__DICT__.travel?.equipmentCarriage || 'Equipment Carriage'"
                       :value="outboundTravel.equipmentCarriage" />
          <DetailField :label="__DICT__.reference || 'Reference'">
            {{ outboundTravel.coach || (__DICT__.notAvailable || 'Not available') }}
            <button @click="linkToTravel(outboundTravel.coach)" class="btn btn-icon btn-ghost-primary btn-sm ms-1">
              <IconSquareArrowRight/>
            </button>
          </DetailField>
          <DetailField :label="__DICT__.travel?.pickUp || 'Pick Up'">
            {{ outboundTravel.pickUpLocation?.location || (__DICT__.notAvailable || 'Not available') }}<br>
            {{ outboundTravel.pickUpLocation?.date_formatted
                ? outboundTravel.pickUpLocation.date_formatted + ' ' + outboundTravel.pickUpLocation.date_time_formatted
                : (__DICT__.timeNotAvailable || 'Time not available') }}
          </DetailField>
          <DetailField :label="__DICT__.travel?.dropOff || 'Drop Off'">
            {{ outboundTravel.dropOffLocation?.location || (__DICT__.notAvailable || 'Not available') }}<br>
            {{ outboundTravel.dropOffLocation?.date_formatted
                ? outboundTravel.dropOffLocation.date_formatted + ' ' + outboundTravel.dropOffLocation.date_time_formatted
                : (__DICT__.timeNotAvailable || 'Time not available') }}
          </DetailField>
        </template>
        <div class="mb-6 text-muted" v-else>
          {{ __DICT__.ownTransport || 'Own Transport' }}
        </div>
      </div>
      <p v-else class="text-muted">{{ __DICT__.travel?.noOutbound || 'No outbound travel information available' }}</p>

      <div v-if="inboundTravel" class="mt-3">
        <h5>{{ __DICT__.travel?.inboundJourney || 'Inbound Journey' }}</h5>
        <template v-if="inboundTravel.transportType">
          <DetailField :label="__DICT__.travel?.type || 'Type'"
                       :value="inboundTravel.transportType || (__DICT__.notAvailable || 'Not available')" />
          <DetailField :label="__DICT__.reference || 'Reference'">
            {{ inboundTravel.coach || (__DICT__.notAvailable || 'Not available') }}
            <button @click="linkToTravel(inboundTravel.coach)" class="btn btn-icon btn-ghost-primary btn-sm ms-1">
              <IconSquareArrowRight/>
            </button>
          </DetailField>
          <DetailField :label="__DICT__.travel?.pickUp || 'Pick Up'">
            {{ inboundTravel.pickUpLocation?.location || (__DICT__.notAvailable || 'Not available') }}<br>
            {{ inboundTravel.pickUpLocation?.date_formatted
                ? inboundTravel.pickUpLocation.date_formatted + ' ' + inboundTravel.pickUpLocation.date_time_formatted
                : (__DICT__.timeNotAvailable || 'Time not available') }}
          </DetailField>
          <DetailField :label="__DICT__.travel?.dropOff || 'Drop Off'">
            {{ inboundTravel.dropOffLocation?.location || (__DICT__.notAvailable || 'Not available') }}<br>
            {{ inboundTravel.dropOffLocation?.date_formatted
                ? inboundTravel.dropOffLocation.date_formatted + ' ' + inboundTravel.dropOffLocation.date_time_formatted
                : (__DICT__.timeNotAvailable || 'Time not available') }}
          </DetailField>
        </template>
        <div class="mb-6 text-muted" v-else>
          {{ __DICT__.ownTransport || 'Own Transport' }}
        </div>
      </div>
      <p v-else class="text-muted">{{ __DICT__.travel?.noInbound || 'No inbound travel information available' }}</p>
  </Card>
</template>